import React from "react";
import { Descriptions, Tag } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import {
    BuyerBriefIntake,
    SUPPLIER_TYPE_LABELS,
    EVALUATION_GOAL_LABELS,
    BRIEF_ADDON_LABELS,
    SupplierTypeGroup,
    EvaluationGoal,
    BriefAddon,
} from "../../../types/BuyerBriefTypes";
import { UNIVERSAL_TOPICS, CONDITIONAL_TOPICS } from "../topicConfig";

interface Props {
    intake: Partial<BuyerBriefIntake>;
}

const ReviewStep: React.FC<Props> = ({ intake }) => {
    const allTopics = [
        ...UNIVERSAL_TOPICS,
        ...(intake.supplierType ? CONDITIONAL_TOPICS[intake.supplierType as SupplierTypeGroup] || [] : []),
    ];

    const selectedTopicLabels = (intake.selectedTopics || [])
        .map((id) => allTopics.find((t) => t.id === id)?.label || id)
        .join(", ");

    const selectedAddonLabels = (intake.selectedAddons || [])
        .map((a) => BRIEF_ADDON_LABELS[a as BriefAddon] || a)
        .join(", ");

    const isValid = !!(intake.supplierName && intake.supplierType && intake.evaluationGoal);

    const CheckIcon = ({ ok }: { ok: boolean }) =>
        ok ? (
            <CheckCircleFilled style={{ color: "#5ad81d" }} />
        ) : (
            <CloseCircleFilled style={{ color: "#ff4d4f" }} />
        );

    return (
        <div>
            <h3>Review & Submit</h3>
            <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <CheckIcon ok={!!intake.supplierName} />
                    <span>Supplier name provided</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <CheckIcon ok={!!intake.supplierType} />
                    <span>Supplier type selected</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <CheckIcon ok={!!intake.evaluationGoal} />
                    <span>Evaluation goal selected</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckIcon ok={(intake.selectedTopics || []).length > 0} />
                    <span>At least one research topic selected</span>
                </div>
            </div>
            <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Supplier Name">
                    {intake.supplierName || <em>Not set</em>}
                </Descriptions.Item>
                <Descriptions.Item label="Website">
                    {intake.supplierWebsite || <em>None</em>}
                </Descriptions.Item>
                <Descriptions.Item label="Supplier Type">
                    {intake.supplierType
                        ? SUPPLIER_TYPE_LABELS[intake.supplierType as SupplierTypeGroup]
                        : <em>Not selected</em>}
                </Descriptions.Item>
                <Descriptions.Item label="Evaluation Goal">
                    {intake.evaluationGoal
                        ? EVALUATION_GOAL_LABELS[intake.evaluationGoal as EvaluationGoal]
                        : <em>Not selected</em>}
                </Descriptions.Item>
                <Descriptions.Item label="Research Topics">
                    {selectedTopicLabels || <em>None selected</em>}
                </Descriptions.Item>
                <Descriptions.Item label="Add-ons">
                    {selectedAddonLabels || <em>None</em>}
                </Descriptions.Item>
            </Descriptions>
            {!isValid && (
                <Tag color="error" style={{ marginTop: 16 }}>
                    Please complete all required fields before submitting.
                </Tag>
            )}
        </div>
    );
};

export default ReviewStep;
